import {
	faCalendar,
	faDownload,
	faExclamationTriangle,
	faRotateRight,
	faTrash,
	faUpload,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import JSZip from 'jszip';
import { PDFDocument } from 'pdf-lib';
import React, { useEffect, useRef } from 'react';
import { Spinner } from 'react-bootstrap';
import { tss } from 'tss-react';

import * as apiDelete from 'api/report/Deletev1filesdeleteV1';
import * as apiVesselPages from 'api/report/Getv1vesselPages';
import LoadingAnimation from 'components/desktop/_general/Loading/LoadingAnimation';
import Modal from 'components/desktop/_general/Modal/Modal';
import ReportsTable from 'containers/desktop/Reports/ReportsTable';
import * as Report from 'containers/desktop/Reports/types/Report';
import * as ReportGroup from 'containers/desktop/Reports/types/ReportGroup';
import * as ReportGroupList from 'containers/desktop/Reports/types/ReportGroupList';
import * as ReportList from 'containers/desktop/Reports/types/ReportList';
import useState from 'containers/desktop/Reports/useState';
import { EAlertType } from 'enums/alertType';
import { Functor } from 'functions/Functor';
import { fireAlert } from 'functions/fireAlert';
import { useJwt } from 'hooks/useJwt';
import useTranslation from 'hooks/useTranslation';

const Reports = () => {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const { state, handlers } = useState();
	const { accessJwt } = useJwt();
	const PAGE_SIZE = 10;
	const [availableMonths, setAvailableMonths] = React.useState<string[]>([]);
	const [hasProcessing, setHasProcessing] = React.useState<boolean>(false);
	const [existingReportForMonth, setExistingReportForMonth] = React.useState<Report.Type | null>(
		null
	);
	const [existingReportsForMonth, setExistingReportsForMonth] = React.useState<Report.Type[]>([]);
	const [errorModal, setErrorModal] = React.useState<{
		open: boolean;
		error: Report.UploadError | null;
		fileName: string;
	}>({ open: false, error: null, fileName: '' });
	const [shortcutMonths, setShortcutMonths] = React.useState<{
		lastMonth: string | null;
		monthBeforeLast: string | null;
		nextMonthAfterLatest: string | null;
	}>({
		lastMonth: null,
		monthBeforeLast: null,
		nextMonthAfterLatest: null,
	});
	const [downloadingMonth, setDownloadingMonth] = React.useState<string | null>(null);
	const [downloadingProgress, setDownloadingProgress] = React.useState<{
		current: number;
		total: number;
		currentGroup: string;
	} | null>(null);
	const [deletingMonth, setDeletingMonth] = React.useState<string | null>(null);
	const [downloadingSource, setDownloadingSource] = React.useState<string | null>(null);
	const stateRef = useRef(state);

	useEffect(() => {
		stateRef.current = state;
	}, [state]);

	useEffect(() => {
		initialize();
	}, []);

	useEffect(() => {
		if (!hasProcessing) {
			return;
		}

		const intervalId = setInterval(() => {
			const currentState = stateRef.current;
			initialize(
				currentState.search,
				currentState.status,
				currentState.filterMonth,
				currentState.page,
				true
			);
		}, 5000);

		return () => {
			clearInterval(intervalId);
		};
	}, [hasProcessing]);

	const initialize = async (
		searchTerm: string = state.search,
		statusFilter: string = state.status,
		monthFilter: string = state.filterMonth,
		page: number = state.page,
		silentUpdate: boolean = false
	) => {
		!silentUpdate && handlers.setIsLoading(true);
		const allReports = await ReportList.getFromApi();

		// Filter out 'source' status reports from display
		const displayReports = allReports.filter((report) => report.status !== 'source');

		const hasProcessingReports = displayReports.some(
			(report) => report.status === 'processing'
		);
		setHasProcessing(hasProcessingReports);

		const filteredGroups = Functor.From(displayReports)
			.map(ReportGroupList.createFromReportList)
			.map(ReportGroupList.orderByDate)
			.map((groups) => ReportGroupList.filterBySearch(groups, searchTerm))
			.map((groups) => ReportGroupList.filterByStatus(groups, statusFilter))
			.map((groups) => ReportGroupList.filterByMonth(groups, monthFilter))
			.get();

		const totalMonths = filteredGroups.length;
		const totalPages = Math.max(1, Math.ceil(totalMonths / PAGE_SIZE));
		const safePage = Math.min(Math.max(1, page), totalPages);

		const pagedGroups = ReportGroupList.paginate(filteredGroups, safePage, PAGE_SIZE);

		handlers.setTotalReports(totalMonths);
		handlers.setPage(safePage);
		handlers.setReportGroups(pagedGroups);
		handlers.setIsLoading(false);

		await updateAvailableMonths();
	};

	const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (!file) return;
		await handleUpload(file);
		event.target.value = '';
	};

	const getDatesFromMonth = (
		monthString: string
	): { dateStart: string; dateEnd: string } | null => {
		if (!monthString || !monthString.includes('-')) {
			return null;
		}
		const [year, month] = monthString.split('-');
		const yearNum = parseInt(year, 10);
		const monthNum = parseInt(month, 10);

		if (isNaN(monthNum) || isNaN(yearNum) || monthNum < 1 || monthNum > 12) {
			return null;
		}
		const dateStart = `${yearNum}-${month.padStart(2, '0')}-01`;
		const dateEnd = `${yearNum}-${month.padStart(2, '0')}-01`;

		return { dateStart, dateEnd };
	};

	const handleUpload = async (file: File) => {
		if (!accessJwt) {
			console.error('User does not have a valid JWT.');
			return;
		}

		if (!state.selectedMonth) {
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Please select a month before uploading.'
			);
			return;
		}

		const hasProcessingReport = existingReportsForMonth.some(
			(report) => report.status === 'processing'
		);
		if (hasProcessingReport) {
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Cannot upload: A report is currently processing for this month. Please wait until processing is complete before uploading a new file.'
			);
			return;
		}

		const dates = getDatesFromMonth(state.selectedMonth);
		if (!dates) {
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Invalid month selection. Please select a valid month.'
			);
			return;
		}

		handlers.setIsLoading(true);

		if (existingReportsForMonth.length > 0) {
			try {
				// Exclude source files and processing reports from deletion
				const deletableReports = existingReportsForMonth.filter(
					(report) =>
						report.status !== 'processing' &&
						report.status !== 'validate' &&
						report.status !== 'source'
				);

				if (deletableReports.length > 0) {
					const tempGroupList: ReportGroupList.Type = [
						{
							id: state.selectedMonth,
							date: new Date(state.selectedMonth + '-01'),
							reports: deletableReports.map((report) => ({
								...report,
								isSelected: true,
							})),
							isSelected: false,
						},
					];
					await ReportGroupList.deleteFromApi(tempGroupList);
				}
			} catch (error) {
				console.error('Error deleting existing reports:', error);
				await fireAlert(
					EAlertType.warning,
					t('admin:REPORTS'),
					'Failed to delete existing reports. Upload cancelled.'
				);
				handlers.setIsLoading(false);
				return;
			}
		}

		const result = await Report.postFileToApi(
			file,
			accessJwt.email,
			dates.dateStart,
			dates.dateEnd
		);
		if (result.success) {
			handlers.setSelectedMonth('');
			setExistingReportForMonth(null);
			setExistingReportsForMonth([]);

			const collapseElement = document.getElementById('uploadReportCollapse');
			if (collapseElement) {
				if ((window as any).bootstrap && (window as any).bootstrap.Collapse) {
					const bsCollapse =
						(window as any).bootstrap.Collapse.getInstance(collapseElement) ||
						new (window as any).bootstrap.Collapse(collapseElement, {
							toggle: false,
						});
					bsCollapse.hide();
				} else {
					collapseElement.classList.add('collapse');
					collapseElement.classList.remove('show');
				}
			}

			await fireAlert(EAlertType.success, t('admin:REPORTS'), t('admin:FILE_UPLOADED'));
			await initialize();
		} else {
			if (result.error && result.error.status === 422) {
				setErrorModal({
					open: true,
					error: result.error,
					fileName: file.name,
				});
			} else {
				await fireAlert(
					EAlertType.warning,
					t('admin:REPORTS'),
					t('admin:FILE_UPLOAD_PROBLEM')
				);
			}
		}
		handlers.setIsLoading(false);
	};

	const handleDragOver = (e: React.DragEvent) => {
		e.preventDefault();
		handlers.setIsDragOver(true);
	};

	const handleDragLeave = (e: React.DragEvent) => {
		e.preventDefault();
		handlers.setIsDragOver(false);
	};

	const handleDrop = (e: React.DragEvent) => {
		e.preventDefault();
		handlers.setIsDragOver(false);
		const hasProcessingReport = existingReportsForMonth.some(
			(report) => report.status === 'processing' || report.status === 'validate'
		);
		if (hasProcessingReport) {
			return;
		}
		const files = e.dataTransfer.files;
		if (files.length > 0) {
			handleUpload(files[0]);
		}
	};

	const getAllAvailableMonths = async (): Promise<string[]> => {
		const allReports = await ReportList.getFromApi();
		const allGroups = ReportGroupList.createFromReportList(allReports);
		const months = allGroups.map((group) => group.id).filter((id) => id);
		return [...new Set(months)].sort().reverse();
	};

	const updateAvailableMonths = async () => {
		const months = await getAllAvailableMonths();
		setAvailableMonths(months);
		updateShortcutMonths(months);
	};

	const formatMonthKey = (year: number, month: number): string => {
		return `${year}-${month.toString().padStart(2, '0')}`;
	};

	const getMonthFromKey = (monthKey: string): { year: number; month: number } | null => {
		if (!monthKey || !monthKey.includes('-')) {
			return null;
		}
		const [yearStr, monthStr] = monthKey.split('-');
		const year = parseInt(yearStr, 10);
		const month = parseInt(monthStr, 10);
		if (isNaN(year) || isNaN(month) || month < 1 || month > 12) {
			return null;
		}
		return { year, month };
	};

	const addMonths = (
		year: number,
		month: number,
		monthsToAdd: number
	): { year: number; month: number } => {
		const date = new Date(year, month - 1, 1);
		date.setMonth(date.getMonth() + monthsToAdd);
		return { year: date.getFullYear(), month: date.getMonth() + 1 };
	};

	const updateShortcutMonths = (availableMonthsList: string[]) => {
		const now = new Date();
		const currentYear = now.getFullYear();
		const currentMonth = now.getMonth() + 1;
		const currentMonthKey = formatMonthKey(currentYear, currentMonth);

		const lastMonthDate = addMonths(currentYear, currentMonth, -1);
		const lastMonthKey = formatMonthKey(lastMonthDate.year, lastMonthDate.month);

		const monthBeforeLastDate = addMonths(currentYear, currentMonth, -2);
		const monthBeforeLastKey = formatMonthKey(
			monthBeforeLastDate.year,
			monthBeforeLastDate.month
		);

		const excludedMonths = new Set([currentMonthKey, lastMonthKey, monthBeforeLastKey]);
		const validMonths = availableMonthsList.filter((m) => !excludedMonths.has(m));

		let nextMonthAfterLatest: string | null = null;
		if (validMonths.length > 0) {
			const sortedMonths = [...validMonths].sort((a, b) => {
				const aDate = getMonthFromKey(a);
				const bDate = getMonthFromKey(b);
				if (!aDate || !bDate) return 0;
				if (aDate.year !== bDate.year) return bDate.year - aDate.year;
				return bDate.month - aDate.month;
			});

			const latestMonth = sortedMonths[0];
			const latestDate = getMonthFromKey(latestMonth);
			if (latestDate) {
				const nextDate = addMonths(latestDate.year, latestDate.month, 1);
				nextMonthAfterLatest = formatMonthKey(nextDate.year, nextDate.month);
			}
		}

		setShortcutMonths({
			lastMonth: lastMonthKey,
			monthBeforeLast: monthBeforeLastKey,
			nextMonthAfterLatest,
		});
	};

	const formatReportPeriodMonth = (monthKey: string): string => {
		if (!monthKey || !monthKey.includes('-')) {
			return monthKey;
		}
		const [year, month] = monthKey.split('-');
		const monthNum = parseInt(month, 10);
		if (isNaN(monthNum) || monthNum < 1 || monthNum > 12) {
			return monthKey;
		}
		const date = new Date(parseInt(year, 10), monthNum - 1, 1);
		return date.toLocaleString('default', { month: 'long', year: 'numeric' });
	};

	const extractPdfFromZip = async (zipBlob: Blob): Promise<ArrayBuffer | null> => {
		try {
			const zip = await JSZip.loadAsync(zipBlob);
			const pdfEntry = Object.values(zip.files).find(
				(f) => !f.dir && f.name.toLowerCase().endsWith('.pdf')
			);
			if (!pdfEntry) {
				return null;
			}
			return await pdfEntry.async('arraybuffer');
		} catch (error) {
			console.error('Error extracting PDF from zip', error);
			return null;
		}
	};

	const generateFileName = (
		report: Report.Type,
		type: 'full' | 'fleet' | 'vessel',
		vesselName?: string
	): string => {
		const dateStart = report.dateStart;
		let year = '';
		let month = '';

		if (dateStart && dateStart.includes('-')) {
			const parts = dateStart.split('-');
			if (parts.length >= 2) {
				year = parts[0] || '';
				month = (parts[1] || '').padStart(2, '0');
			}
		}

		if (!year || !month) {
			const now = new Date();
			year = now.getFullYear().toString();
			month = (now.getMonth() + 1).toString().padStart(2, '0');
		}

		const safeGroupName =
			(report.groupName || 'unknown')
				.replace(/[<>:"/\\|?*]/g, '_')
				.replace(/\s+/g, '_')
				.replace(/_{2,}/g, '_')
				.replace(/^_+|_+$/g, '')
				.trim() || 'unknown';

		const baseName = `${year}-${month}-${safeGroupName}`;

		if (type === 'full') {
			return `${baseName}.pdf`;
		} else if (type === 'fleet') {
			return `${baseName}-fleet.pdf`;
		} else if (type === 'vessel' && vesselName) {
			const safeVesselName =
				vesselName
					.replace(/[<>:"/\\|?*]/g, '_')
					.replace(/\s+/g, '_')
					.replace(/_{2,}/g, '_')
					.replace(/^_+|_+$/g, '')
					.trim() || 'vessel';
			return `${baseName}-${safeVesselName}.pdf`;
		}

		return `${report.id.replace(/[^a-zA-Z0-9-_]/g, '_')}.pdf`;
	};

	const handleDownloadMonth = async (reportGroup: ReportGroup.Type) => {
		// Check if all reports are done or failed (not processing or pending)
		const allProcessed = reportGroup.reports.every(
			(report) =>
				report.status === 'done' || report.status === 'failed' || report.status === 'error'
		);
		if (!allProcessed) {
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Cannot download: Some reports for this month are still processing. Please wait until all reports are processed or failed.'
			);
			return;
		}

		setDownloadingMonth(reportGroup.id);
		setDownloadingProgress(null);
		try {
			const monthZip = new JSZip();
			const monthFolder = monthZip.folder(reportGroup.id);
			if (!monthFolder) {
				throw new Error('Failed to create month folder');
			}

			// Group reports by groupName
			const reportsByGroup = new Map<string, Report.Type[]>();
			for (const report of reportGroup.reports) {
				const groupName = report.groupName || 'unknown';
				if (!reportsByGroup.has(groupName)) {
					reportsByGroup.set(groupName, []);
				}
				reportsByGroup.get(groupName)!.push(report);
			}

			const totalGroups = reportsByGroup.size;
			let currentGroupIndex = 0;

			// Process each group
			for (const [groupName, reports] of reportsByGroup.entries()) {
				currentGroupIndex++;
				setDownloadingProgress({
					current: currentGroupIndex,
					total: totalGroups,
					currentGroup: groupName,
				});
				// Check if all reports in this group are failed
				const allFailed = reports.every(
					(report) => report.status === 'failed' || report.status === 'error'
				);

				// Determine folder name: add "failed_" prefix if all reports failed
				const folderName = allFailed ? `failed_${groupName}` : groupName;
				const groupFolder = monthFolder.folder(folderName);
				if (!groupFolder) {
					console.error(`Failed to create folder for group: ${folderName}`);
					continue;
				}

				// If all reports failed, create empty folder and skip PDF processing
				if (allFailed) {
					// Folder is created but left empty
					continue;
				}

				// For each report in this group, create all PDFs (full, fleet, vessels)
				for (const report of reports) {
					// Only process done reports
					if (report.status !== 'done') {
						continue;
					}

					try {
						const zipBlob = await ReportGroupList.downloadSingleBlob(report.id);
						if (!zipBlob) {
							console.error(`Failed to download report: ${report.id}`);
							continue;
						}

						const pdfArrayBuffer = await extractPdfFromZip(zipBlob);
						if (!pdfArrayBuffer) {
							console.error(`No PDF found in report: ${report.id}`);
							continue;
						}

						const pdfDoc = await PDFDocument.load(pdfArrayBuffer);
						const pageCount = pdfDoc.getPageCount();

						// 1. Add full report PDF
						const fullPdfBytes = await pdfDoc.save();
						const fullFileName = generateFileName(report, 'full');
						groupFolder.file(fullFileName, fullPdfBytes);

						// 2. Add overview/fleet PDF (page 1)
						if (pageCount >= 1) {
							const overviewPdf = await PDFDocument.create();
							const [overviewPage] = await overviewPdf.copyPages(pdfDoc, [0]);
							overviewPdf.addPage(overviewPage);
							const overviewPdfBytes = await overviewPdf.save();
							const overviewFileName = generateFileName(report, 'fleet');
							groupFolder.file(overviewFileName, overviewPdfBytes);
						}

						// 3. Add individual vessel PDFs
						try {
							const vesselData = await apiVesselPages.callApi(report.id);

							for (const vessel of vesselData) {
								const pageIndex = vessel.page_index - 1; // Convert to 0-based index

								// Skip page 1 (overview) as we already added it
								if (pageIndex === 0) {
									continue;
								}

								if (pageIndex >= 0 && pageIndex < pageCount) {
									const vesselPdf = await PDFDocument.create();
									const [vesselPage] = await vesselPdf.copyPages(pdfDoc, [
										pageIndex,
									]);
									vesselPdf.addPage(vesselPage);
									const vesselPdfBytes = await vesselPdf.save();
									const vesselFileName = generateFileName(
										report,
										'vessel',
										vessel.page_name
									);
									groupFolder.file(vesselFileName, vesselPdfBytes);
								}
							}
						} catch (error) {
							console.error(
								`Error loading vessel pages for report ${report.id}:`,
								error
							);
							// Continue even if vessel pages fail to load
						}
					} catch (error) {
						console.error(`Error processing report ${report.id}:`, error);
					}
				}
			}

			// Generate and download the zip
			const zipBlob = await monthZip.generateAsync({ type: 'blob' });
			const fileURL = URL.createObjectURL(zipBlob);
			const link = document.createElement('a');
			link.href = fileURL;

			// Generate filename: air-report-{yyyy}-{mm}.zip
			const [year, month] = reportGroup.id.split('-');
			link.download = `air-report-${year}-${month}.zip`;
			link.click();
			URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error('Error downloading month reports:', error);
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Failed to download month reports. Please try again.'
			);
		} finally {
			setDownloadingMonth(null);
			setDownloadingProgress(null);
		}
	};

	const handleDeleteMonth = async (reportGroup: ReportGroup.Type) => {
		// Check if all reports are done processing (not processing or pending)
		const allDone = reportGroup.reports.every((report) => report.status !== 'processing');
		if (!allDone) {
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Cannot delete: Some reports for this month are still processing.'
			);
			return;
		}

		// Confirm deletion
		const confirmed = window.confirm(
			`Are you sure you want to delete all reports for ${formatReportPeriodMonth(
				reportGroup.id
			)}? This action cannot be undone.`
		);
		if (!confirmed) {
			return;
		}

		setDeletingMonth(reportGroup.id);
		try {
			// Exclude source files from deletion - they should persist
			const reportIds = reportGroup.reports
				.filter((report) => report.status !== 'source')
				.map((report) => report.id);

			if (reportIds.length > 0) {
				await apiDelete.callApi({
					requestBody: {
						fileIds: reportIds,
					},
				});
			}
			await fireAlert(
				EAlertType.success,
				t('admin:REPORTS'),
				`All reports for ${formatReportPeriodMonth(reportGroup.id)} have been deleted.`
			);
			// Refresh the list with current filters
			await initialize(state.search, state.status, state.filterMonth, state.page);
		} catch (error) {
			console.error('Error deleting month reports:', error);
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Failed to delete month reports. Please try again.'
			);
		} finally {
			setDeletingMonth(null);
		}
	};

	const getLatestSourceForMonth = async (monthKey: string): Promise<Report.Type | null> => {
		const allReports = await ReportList.getFromApi();
		const sourceReports = allReports.filter(
			(report) => report.status === 'source' && getMonthKey(report.dateStart) === monthKey
		);

		if (sourceReports.length === 0) {
			return null;
		}

		// Sort by uploadedAt descending and return the latest
		sourceReports.sort((a, b) => b.uploadedAt.getTime() - a.uploadedAt.getTime());
		return sourceReports[0];
	};

	const getMonthKey = (dateStart: string): string => {
		if (!dateStart || !dateStart.includes('-')) {
			return '';
		}
		const parts = dateStart.split('-');
		if (parts.length < 2) {
			return '';
		}
		return `${parts[0]}-${parts[1]}`;
	};

	const handleDownloadSource = async (reportGroup: ReportGroup.Type) => {
		setDownloadingSource(reportGroup.id);
		try {
			const sourceReport = await getLatestSourceForMonth(reportGroup.id);
			if (!sourceReport) {
				await fireAlert(
					EAlertType.warning,
					t('admin:REPORTS'),
					`No source file found for ${formatReportPeriodMonth(reportGroup.id)}.`
				);
				return;
			}

			const blob = await ReportGroupList.downloadSingleBlob(sourceReport.id, 'source');
			if (!blob) {
				await fireAlert(
					EAlertType.warning,
					t('admin:REPORTS'),
					'Failed to download source file. Please try again.'
				);
				return;
			}

			// Check if the blob is a zip file (source files might be wrapped in zip)
			// If it's a zip, extract the CSV; otherwise use the blob directly
			let csvBlob: Blob = blob;
			if (blob.type === 'application/zip' || blob.type === 'application/x-zip-compressed') {
				try {
					const zip = await JSZip.loadAsync(blob);
					// Find the CSV file in the zip
					const csvFile = Object.values(zip.files).find(
						(file) => !file.dir && file.name.toLowerCase().endsWith('.csv')
					);
					if (csvFile) {
						const csvData = await csvFile.async('blob');
						csvBlob = new Blob([csvData], { type: 'text/csv' });
					} else {
						// If no CSV found, try to use the blob as-is
						csvBlob = new Blob([blob], { type: 'text/csv' });
					}
				} catch (error) {
					console.error('Error extracting CSV from zip:', error);
					// If extraction fails, try to use the blob as CSV directly
					csvBlob = new Blob([blob], { type: 'text/csv' });
				}
			} else {
				// Ensure the blob has the correct MIME type for CSV
				csvBlob = new Blob([blob], { type: 'text/csv' });
			}

			const fileURL = URL.createObjectURL(csvBlob);
			const link = document.createElement('a');
			link.href = fileURL;

			// Generate filename: source-{yyyy}-{mm}.csv
			const [year, month] = reportGroup.id.split('-');
			link.download = `source-${year}-${month}.csv`;
			link.click();
			URL.revokeObjectURL(fileURL);
		} catch (error) {
			console.error('Error downloading source file:', error);
			await fireAlert(
				EAlertType.warning,
				t('admin:REPORTS'),
				'Failed to download source file. Please try again.'
			);
		} finally {
			setDownloadingSource(null);
		}
	};

	const checkExistingReportForMonth = async (month: string) => {
		if (!month) {
			setExistingReportForMonth(null);
			setExistingReportsForMonth([]);
			return;
		}
		const allReports = await ReportList.getFromApi();
		// Filter out source reports when checking existing reports
		const displayReports = allReports.filter((report) => report.status !== 'source');
		const allGroups = ReportGroupList.createFromReportList(displayReports);

		const monthGroup = allGroups.find((group) => group.id === month);
		if (monthGroup && monthGroup.reports.length > 0) {
			setExistingReportsForMonth(monthGroup.reports);
			setExistingReportForMonth(monthGroup.reports[0]);
		} else {
			setExistingReportForMonth(null);
			setExistingReportsForMonth([]);
		}
	};

	useEffect(() => {
		checkExistingReportForMonth(state.selectedMonth);
	}, [state.selectedMonth]);

	return (
		<div className={cx('container', classes.container)}>
			<LoadingAnimation isLoading={state.isLoading}></LoadingAnimation>
			<div className="row">
				<div className="col-12 mb-4">
					<div className="accordion" id="reportsAccordion">
						<div className="accordion-item">
							<h2 className="accordion-header" id="uploadReportHeading">
								<button
									className="accordion-button collapsed bg-primary text-white"
									type="button"
									data-bs-toggle="collapse"
									data-bs-target="#uploadReportCollapse"
									aria-expanded="false"
									aria-controls="uploadReportCollapse"
								>
									<FontAwesomeIcon
										icon={faUpload}
										style={{ marginRight: '1rem' }}
									></FontAwesomeIcon>{' '}
									Upload File
								</button>
							</h2>
							<div
								id="uploadReportCollapse"
								className="accordion-collapse collapse"
								aria-labelledby="uploadReportHeading"
								data-bs-parent="#reportsAccordion"
							>
								<div className="accordion-body d-flex flex-column align-items-center">
									<div className="mb-2" style={{ width: '30%' }}>
										<label htmlFor="month-selector" className="form-label">
											Select Month
										</label>
										<input
											type="month"
											id="month-selector"
											className="form-control"
											value={state.selectedMonth}
											onChange={(e) =>
												handlers.setSelectedMonth(e.target.value)
											}
											onClick={(e) => {
												e.currentTarget.focus();
												if (
													'showPicker' in e.currentTarget &&
													typeof (e.currentTarget as any).showPicker ===
														'function'
												) {
													try {
														(e.currentTarget as any).showPicker();
													} catch (err) {
														console.log(err);
													}
												}
											}}
											onFocus={(e) => {
												if (
													'showPicker' in e.currentTarget &&
													typeof (e.currentTarget as any).showPicker ===
														'function'
												) {
													try {
														(e.currentTarget as any).showPicker();
													} catch (err) {
														console.log(err);
													}
												}
											}}
											style={{
												cursor: 'pointer',
												width: '100%',
											}}
											required
										/>
										<div className="mt-2">
											<div
												className="d-flex gap-2 flex-wrap align-items-center"
												style={{ justifyContent: 'center' }}
											>
												<small className="text-muted d-block mb-1">
													Suggestions:
												</small>
												{shortcutMonths.lastMonth && (
													<button
														type="button"
														className="btn btn-sm btn-outline-secondary"
														onClick={() =>
															handlers.setSelectedMonth(
																shortcutMonths.lastMonth!
															)
														}
														title={formatReportPeriodMonth(
															shortcutMonths.lastMonth
														)}
													>
														Last Month
													</button>
												)}
												{shortcutMonths.monthBeforeLast && (
													<button
														type="button"
														className="btn btn-sm btn-outline-secondary"
														onClick={() =>
															handlers.setSelectedMonth(
																shortcutMonths.monthBeforeLast!
															)
														}
													>
														{formatReportPeriodMonth(
															shortcutMonths.monthBeforeLast
														)}
													</button>
												)}
												{shortcutMonths.nextMonthAfterLatest && (
													<button
														type="button"
														className="btn btn-sm btn-outline-secondary"
														onClick={() =>
															handlers.setSelectedMonth(
																shortcutMonths.nextMonthAfterLatest!
															)
														}
													>
														{formatReportPeriodMonth(
															shortcutMonths.nextMonthAfterLatest
														)}
													</button>
												)}
											</div>
										</div>
									</div>
									{existingReportsForMonth.length > 0 &&
										!existingReportsForMonth.some(
											(report) =>
												report.status === 'processing' ||
												report.status === 'validate'
										) && (
											<div className="alert alert-warning mb-3" role="alert">
												<small>
													<strong>Warning:</strong>{' '}
													{existingReportsForMonth.length === 1
														? 'A report already exists'
														: `${existingReportsForMonth.length} reports already exist`}{' '}
													for this month. Uploading a new file will
													replace
													{existingReportsForMonth.length === 1
														? ` the existing report (${existingReportsForMonth[0].id}).`
														: ' all existing reports.'}
												</small>
											</div>
										)}
									{existingReportsForMonth.some(
										(report) =>
											report.status === 'processing' ||
											report.status === 'validate'
									) && (
										<div className="alert alert-danger mb-3" role="alert">
											<small>
												<strong>Upload Blocked:</strong> A report is
												currently processing for this month. You cannot
												upload a new file until processing is complete.
											</small>
										</div>
									)}
									<div
										className={cx(
											classes.uploadArea,
											state.isDragOver && classes.uploadAreaDragOver,
											(!state.selectedMonth ||
												existingReportsForMonth.some(
													(report) =>
														report.status === 'processing' ||
														report.status === 'validate'
												)) &&
												classes.uploadAreaDisabled
										)}
										onDragOver={handleDragOver}
										onDragLeave={handleDragLeave}
										onDrop={handleDrop}
									>
										<FontAwesomeIcon
											icon={faUpload}
											size="4x"
											className={classes.uploadIcon}
										/>
										<h4>{t('admin:DRAG_AND_DROP_FILE')}</h4>
										<input
											type="file"
											id="file-upload"
											onChange={handleFileChange}
											style={{ display: 'none' }}
											disabled={
												!state.selectedMonth ||
												existingReportsForMonth.some(
													(report) =>
														report.status === 'processing' ||
														report.status === 'validate'
												)
											}
										/>
										<label
											htmlFor="file-upload"
											className={cx(
												'btn btn-primary mt-2',
												(!state.selectedMonth ||
													existingReportsForMonth.some(
														(report) =>
															report.status === 'processing' ||
															report.status === 'validate'
													)) &&
													'disabled'
											)}
											style={{
												pointerEvents:
													state.selectedMonth &&
													!existingReportsForMonth.some(
														(report) =>
															report.status === 'processing' ||
															report.status === 'validate'
													)
														? 'auto'
														: 'none',
												opacity:
													state.selectedMonth &&
													!existingReportsForMonth.some(
														(report) =>
															report.status === 'processing' ||
															report.status === 'validate'
													)
														? 1
														: 0.6,
											}}
										>
											{t('admin:SELECT_FILE')}
										</label>
										{!state.selectedMonth && (
											<p className="text-muted mt-2 small">
												Please select a month first
											</p>
										)}
										{existingReportsForMonth.some(
											(report) =>
												report.status === 'processing' ||
												report.status === 'validate'
										) && (
											<p className="text-danger mt-2 small">
												<strong>Upload blocked:</strong> A report is
												currently processing for this month. Please wait
												until processing is complete.
											</p>
										)}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<hr />
				<div className="col-12">
					<h3>{t('admin:YOUR_REPORTS')}</h3>
					<div className={cx(classes.buttonRow, 'flex-wrap gap-2')}>
						<div className="flex-grow-1">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder={t('admin:SEARCH') ?? 'Search by name or user'}
								value={state.search}
								onChange={(e) => {
									const value = e.target.value;
									handlers.setSearch(value);
									handlers.setPage(1);
									initialize(value, state.status, state.filterMonth, 1);
								}}
							/>
						</div>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto', minWidth: '150px' }}
							value={state.filterMonth}
							onChange={(e) => {
								const value = e.target.value;
								handlers.setFilterMonth(value);
								handlers.setPage(1);
								initialize(state.search, state.status, value, 1);
							}}
						>
							<option value="">All Months</option>
							{availableMonths.map((month) => {
								const [year, monthNum] = month.split('-');
								const monthName = new Date(
									parseInt(year),
									parseInt(monthNum) - 1,
									1
								).toLocaleString('default', { month: 'long', year: 'numeric' });
								return (
									<option key={month} value={month}>
										{monthName}
									</option>
								);
							})}
						</select>
						<select
							className="form-select form-select-sm"
							style={{ width: 'auto', minWidth: '150px' }}
							value={state.status}
							onChange={(e) => {
								const value = e.target.value;
								handlers.setStatus(value);
								handlers.setPage(1);
								initialize(state.search, value, state.filterMonth, 1);
							}}
						>
							<option value="all">All Status</option>
							<option value="done">Available</option>
							<option value="processing">Processing</option>
							<option value="failed">Failed</option>
							<option value="error">Error</option>
							<option value="pending">Pending</option>
						</select>
						<button
							className="btn btn-sm btn-outline-secondary"
							onClick={async () => {
								await initialize();
							}}
							title={'Refresh'}
						>
							<FontAwesomeIcon icon={faRotateRight} className="me-1" />
							{'Refresh'}
						</button>
					</div>
					<div className="accordion" id="reportsMonthAccordion">
						{state.reportGroups.map(
							(reportGroup: ReportGroup.Type, groupIndex: number) => {
								const monthName = formatReportPeriodMonth(reportGroup.id);
								const collapseId = `collapse-${reportGroup.id}-${groupIndex}`;
								return (
									<div key={reportGroup.id} className="accordion-item">
										<h2
											className="accordion-header"
											id={`heading-${reportGroup.id}`}
										>
											<button
												className="accordion-button collapsed"
												type="button"
												data-bs-toggle="collapse"
												data-bs-target={`#${collapseId}`}
												aria-expanded="false"
												aria-controls={collapseId}
												disabled={deletingMonth === reportGroup.id}
											>
												<span>
													<FontAwesomeIcon
														icon={faCalendar}
														style={{ marginRight: '1rem' }}
													></FontAwesomeIcon>
													{monthName} ({reportGroup.reports.length}{' '}
													{reportGroup.reports.length === 1
														? 'Fleet'
														: 'Fleets'}
													)
												</span>
											</button>
										</h2>
										<div
											id={collapseId}
											className="accordion-collapse collapse"
											aria-labelledby={`heading-${reportGroup.id}`}
											data-bs-parent="#reportsMonthAccordion"
										>
											<div className="accordion-body">
												<div className="d-flex gap-2 mb-3">
													<button
														className="btn btn-primary"
														style={{ flex: '3' }}
														onClick={() => {
															handleDownloadMonth(reportGroup);
														}}
														disabled={
															!reportGroup.reports.every(
																(r) =>
																	r.status === 'done' ||
																	r.status === 'failed' ||
																	r.status === 'error'
															) ||
															downloadingMonth === reportGroup.id ||
															deletingMonth === reportGroup.id
														}
														title={
															reportGroup.reports.every(
																(r) =>
																	r.status === 'done' ||
																	r.status === 'failed' ||
																	r.status === 'error'
															)
																? `Download all reports for ${monthName} (including failed groups)`
																: 'All reports must be processed or failed before downloading'
														}
													>
														{downloadingMonth === reportGroup.id &&
														downloadingProgress ? (
															<div
																style={{
																	position: 'relative',
																	width: '100%',
																	display: 'flex',
																	alignItems: 'center',
																	justifyContent: 'center',
																	zIndex: 1,
																}}
															>
																<span
																	style={{
																		marginRight: '8px',
																		fontWeight: 'bold',
																	}}
																>
																	{Math.round(
																		(downloadingProgress.current /
																			downloadingProgress.total) *
																			100
																	)}
																	%
																</span>
																<span
																	style={{
																		fontSize: '0.85em',
																		opacity: 0.9,
																	}}
																>
																	({downloadingProgress.current}/
																	{downloadingProgress.total}){' '}
																	{
																		downloadingProgress.currentGroup
																	}
																</span>
																<div
																	style={{
																		position: 'absolute',
																		left: 0,
																		top: 0,
																		height: '100%',
																		width: `${
																			(downloadingProgress.current /
																				downloadingProgress.total) *
																			100
																		}%`,
																		backgroundColor:
																			'rgba(0, 0, 0, 0.2)',
																		transition:
																			'width 0.3s ease',
																		zIndex: -1,
																	}}
																/>
															</div>
														) : downloadingMonth === reportGroup.id ? (
															<>
																<Spinner
																	size="sm"
																	animation="border"
																	className="me-2"
																/>
																Downloading...
															</>
														) : (
															<>
																<FontAwesomeIcon
																	icon={faDownload}
																	className="me-2"
																/>
																Download all data from {monthName}
															</>
														)}
													</button>
													<button
														className="btn btn-outline-secondary"
														style={{ flex: '1' }}
														onClick={() => {
															handleDownloadSource(reportGroup);
														}}
														disabled={
															downloadingSource === reportGroup.id ||
															deletingMonth === reportGroup.id
														}
														title={`Download source CSV file for ${monthName}`}
													>
														{downloadingSource === reportGroup.id ? (
															<>
																<Spinner
																	size="sm"
																	animation="border"
																	className="me-2"
																/>
																Downloading...
															</>
														) : (
															<>
																<FontAwesomeIcon
																	icon={faDownload}
																	className="me-2"
																/>
																Source CSV
															</>
														)}
													</button>
													<button
														className="btn btn-outline-danger"
														style={{ flex: '1' }}
														onClick={() => {
															handleDeleteMonth(reportGroup);
														}}
														disabled={
															!reportGroup.reports.every(
																(r) => r.status !== 'processing'
															) || deletingMonth === reportGroup.id
														}
														title={
															reportGroup.reports.every(
																(r) =>
																	r.status !== 'processing' &&
																	r.status !== 'validate'
															)
																? `Delete all reports for ${monthName}`
																: 'All reports must be processed before deleting'
														}
													>
														{deletingMonth === reportGroup.id ? (
															<>
																<Spinner
																	size="sm"
																	animation="border"
																	className="me-2"
																/>
																Deleting...
															</>
														) : (
															<>
																<FontAwesomeIcon
																	icon={faTrash}
																	className="me-2"
																/>
																Delete
															</>
														)}
													</button>
												</div>
												<ReportsTable
													reportGroups={[reportGroup]}
													setReportGroups={(groups) => {
														const updatedGroups =
															state.reportGroups.map((g) =>
																g.id === reportGroup.id
																	? groups[0]
																	: g
															);
														handlers.setReportGroups(updatedGroups);
													}}
													onRefresh={initialize}
													disabled={deletingMonth === reportGroup.id}
												/>
											</div>
										</div>
									</div>
								);
							}
						)}
					</div>
					<Pagination
						totalItems={state.totalReports}
						pageSize={PAGE_SIZE}
						currentPage={state.page}
						onChange={(p) => {
							handlers.setPage(p);
							initialize(state.search, state.status, state.filterMonth, p);
						}}
						t={t}
					/>
				</div>
			</div>
			<Modal
				headerIcon={<FontAwesomeIcon icon={faExclamationTriangle} />}
				headerText="Upload Error - CSV Validation Failed"
				bodyContent={
					errorModal.error ? (
						<div>
							<div className="alert alert-danger mb-3" role="alert">
								<strong>Error:</strong>{' '}
								{errorModal.error.detail?.message || 'CSV validation failed'}
							</div>
							{errorModal.error.detail?.filename && (
								<div className="mb-3">
									<strong>File:</strong> {errorModal.error.detail.filename}
								</div>
							)}

							{/* Error Type 1: MISSING_COLUMNS */}
							{errorModal.error.detail?.error_type === 1 && (
								<>
									{errorModal.error.detail.missing_columns &&
										errorModal.error.detail.missing_columns.length > 0 && (
											<div className="mb-3">
												<strong className="text-danger">
													Missing Columns:
												</strong>
												<ul className="mt-2">
													{errorModal.error.detail.missing_columns.map(
														(col, idx) => (
															<li key={idx}>{col}</li>
														)
													)}
												</ul>
											</div>
										)}
									{errorModal.error.detail.required_columns &&
										errorModal.error.detail.required_columns.length > 0 && (
											<div className="mb-3">
												<strong>Required Columns:</strong>
												<ul className="mt-2">
													{errorModal.error.detail.required_columns.map(
														(col, idx) => (
															<li key={idx}>{col}</li>
														)
													)}
												</ul>
											</div>
										)}
								</>
							)}

							{/* Error Type 2: INVALID_FIELDS */}
							{errorModal.error.detail?.error_type === 2 && (
								<>
									{errorModal.error.detail.total_rows !== undefined && (
										<div className="mb-3">
											<strong>Total Rows:</strong>{' '}
											{errorModal.error.detail.total_rows}
										</div>
									)}
									{errorModal.error.detail.errors &&
										errorModal.error.detail.errors.length > 0 && (
											<div className="mb-3">
												<strong className="text-danger">
													Validation Errors:
												</strong>
												<div
													className="table-responsive mt-2"
													style={{
														maxHeight: '400px',
														overflowY: 'auto',
													}}
												>
													<table className="table table-sm table-bordered">
														<thead className="table-light">
															<tr>
																<th>Row</th>
																<th>Column</th>
																<th>Value</th>
																<th>Issue</th>
																<th>Expected</th>
															</tr>
														</thead>
														<tbody>
															{errorModal.error.detail.errors.map(
																(err, idx) => (
																	<tr key={idx}>
																		<td>{err.row}</td>
																		<td>{err.column}</td>
																		<td>
																			{err.value || (
																				<em className="text-muted">
																					(empty)
																				</em>
																			)}
																		</td>
																		<td className="text-danger">
																			{err.issue}
																		</td>
																		<td>
																			{err.expected || (
																				<em className="text-muted">
																					-
																				</em>
																			)}
																		</td>
																	</tr>
																)
															)}
														</tbody>
													</table>
												</div>
											</div>
										)}
								</>
							)}

							{/* Error Type 3: INVALID_UNIT */}
							{errorModal.error.detail?.error_type === 3 && (
								<>
									{errorModal.error.detail.invalid_units &&
										errorModal.error.detail.invalid_units.length > 0 && (
											<div className="mb-3">
												<strong className="text-danger">
													Invalid Units:
												</strong>
												<ul className="mt-2">
													{errorModal.error.detail.invalid_units.map(
														(unit, idx) => (
															<li key={idx}>{unit}</li>
														)
													)}
												</ul>
											</div>
										)}
									{errorModal.error.detail.allowed_units &&
										errorModal.error.detail.allowed_units.length > 0 && (
											<div className="mb-3">
												<strong>Allowed Units:</strong>
												<ul className="mt-2">
													{errorModal.error.detail.allowed_units.map(
														(unit, idx) => (
															<li key={idx}>{unit}</li>
														)
													)}
												</ul>
											</div>
										)}
								</>
							)}

							{/* Fallback for errors without error_type (backward compatibility) */}
							{!errorModal.error.detail?.error_type && (
								<>
									{errorModal.error.detail?.total_rows !== undefined && (
										<div className="mb-3">
											<strong>Total Rows:</strong>{' '}
											{errorModal.error.detail.total_rows}
										</div>
									)}
									{errorModal.error.detail?.errors &&
										errorModal.error.detail.errors.length > 0 && (
											<div className="mb-3">
												<strong className="text-danger">
													Validation Errors:
												</strong>
												<div
													className="table-responsive mt-2"
													style={{
														maxHeight: '400px',
														overflowY: 'auto',
													}}
												>
													<table className="table table-sm table-bordered">
														<thead className="table-light">
															<tr>
																<th>Row</th>
																<th>Column</th>
																<th>Value</th>
																<th>Issue</th>
																<th>Expected</th>
															</tr>
														</thead>
														<tbody>
															{errorModal.error.detail.errors.map(
																(err, idx) => (
																	<tr key={idx}>
																		<td>{err.row}</td>
																		<td>{err.column}</td>
																		<td>
																			{err.value || (
																				<em className="text-muted">
																					(empty)
																				</em>
																			)}
																		</td>
																		<td className="text-danger">
																			{err.issue}
																		</td>
																		<td>
																			{err.expected || (
																				<em className="text-muted">
																					-
																				</em>
																			)}
																		</td>
																	</tr>
																)
															)}
														</tbody>
													</table>
												</div>
											</div>
										)}
									{errorModal.error.detail?.missing_columns &&
										errorModal.error.detail.missing_columns.length > 0 && (
											<div className="mb-3">
												<strong className="text-danger">
													Missing Columns:
												</strong>
												<ul className="mt-2">
													{errorModal.error.detail.missing_columns.map(
														(col, idx) => (
															<li key={idx}>{col}</li>
														)
													)}
												</ul>
											</div>
										)}
									{errorModal.error.detail?.required_columns &&
										errorModal.error.detail.required_columns.length > 0 && (
											<div className="mb-3">
												<strong>Required Columns:</strong>
												<ul className="mt-2">
													{errorModal.error.detail.required_columns.map(
														(col, idx) => (
															<li key={idx}>{col}</li>
														)
													)}
												</ul>
											</div>
										)}
								</>
							)}

							{/* Hint (common to all error types) */}
							{errorModal.error.detail?.hint && (
								<div className="alert alert-info mt-3" role="alert">
									<strong>Hint:</strong> {errorModal.error.detail.hint}
								</div>
							)}
						</div>
					) : (
						<div>An error occurred during upload.</div>
					)
				}
				isOpen={errorModal.open}
				close={() => setErrorModal({ open: false, error: null, fileName: '' })}
				size="lg"
			/>
		</div>
	);
};

export default Reports;

const useStyles = tss.create({
	container: { padding: '3rem' },
	title: { marginBottom: '20px' },
	label: {
		display: 'flex',
		flexDirection: 'row',
		justifyContent: 'start',
		alignItems: 'end',
		gap: '10px',
	},
	uploadArea: {
		border: '2px dashed #ccc',
		borderRadius: '8px',
		padding: '40px',
		textAlign: 'center',
		backgroundColor: '#fafafa',
		transition: 'all 0.3s ease',
		cursor: 'pointer',
		width: '60%',
	},
	uploadAreaDragOver: { backgroundColor: '#f0f8ff' },
	uploadAreaDisabled: {
		opacity: 0.6,
		cursor: 'not-allowed',
		pointerEvents: 'none',
	},
	uploadIcon: { color: '#666', marginBottom: '20px' },
	buttonRow: {
		display: 'flex',
		gap: '5px',
		marginBottom: '10px',
		justifyContent: 'end',
	},
});

type PaginationProps = {
	totalItems: number;
	pageSize: number;
	currentPage: number;
	onChange: (page: number) => void;
	t: ReturnType<typeof useTranslation>['t'];
};

const Pagination = ({ totalItems, pageSize, currentPage, onChange, t }: PaginationProps) => {
	const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
	const disablePrev = currentPage <= 1;
	const disableNext = currentPage >= totalPages;

	const goTo = (page: number) => {
		if (page < 1 || page > totalPages) return;
		onChange(page);
	};

	return (
		<nav aria-label="Reports pagination" style={{ marginTop: '2rem' }}>
			<ul className="pagination pagination-sm justify-content-end mb-0">
				<li className={`page-item ${disablePrev ? 'disabled' : ''}`}>
					<button className="page-link" onClick={() => goTo(currentPage - 1)}>
						{t('admin:PREVIOUS') ?? 'Previous'}
					</button>
				</li>
				<li className="page-item disabled">
					<span className="page-link">
						{currentPage} / {totalPages}
					</span>
				</li>
				<li className={`page-item ${disableNext ? 'disabled' : ''}`}>
					<button className="page-link" onClick={() => goTo(currentPage + 1)}>
						{t('admin:NEXT') ?? 'Next'}
					</button>
				</li>
			</ul>
		</nav>
	);
};
